import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, brl, errMsg, imgUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function ProductList({ products, value, onPick }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 80), [products, q]);
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">Produto</p>
      <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar produto" className="pl-9" data-testid="reward-product-search" /></div>
      <div className="max-h-60 divide-y overflow-y-auto rounded-lg border" data-testid="reward-product-list">
        {list.map((p) => (
          <button type="button" key={p.id} onClick={() => onPick(p.id)} data-testid={`reward-product-${p.id}`}
            className={cn("flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors", value === p.id ? "bg-primary/10 text-primary" : "hover:bg-muted")}>
            {p.image_url ? <img src={imgUrl(p.image_url)} alt="" className="h-9 w-9 rounded object-cover" /> : <span className="grid h-9 w-9 place-items-center rounded bg-muted"><Package className="h-4 w-4 opacity-60" /></span>}
            <span className="flex-1 truncate">{p.name}</span><span className="font-mono text-xs">{brl(p.price)}</span>
          </button>
        ))}
        {!list.length && <p className="p-4 text-center text-sm text-muted-foreground">Nenhum produto encontrado.</p>}
      </div>
    </div>
  );
}

export function RewardDialog({ reward, onClose, onSaved }) {
  const [f, setF] = useState({ points: "", value: "", product_id: "" });
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const type = reward?.type;
  useEffect(() => { if (reward) setF({ points: reward.points ?? "", value: reward.type === "discount" ? reward.value ?? "" : "", product_id: reward.product_id || "" }); }, [reward]);
  useEffect(() => { if (type === "product" && !products.length) api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active !== false))).catch(() => setProducts([])); }, [type, products.length]);
  const ok = Number(f.points) >= 1 && (type === "discount" ? Number(f.value) > 0 : !!f.product_id);
  const save = async () => {
    setBusy(true);
    try {
      const body = { type, points: Math.round(Number(f.points)), value: Number(f.value) || 0, product_id: f.product_id || null, active: reward.active ?? true };
      await (reward.id ? api.put(`/loyalty/rewards/${reward.id}`, body) : api.post("/loyalty/rewards", body));
      toast.success("Recompensa salva");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!reward} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reward-dialog">
        <DialogHeader>
          <DialogTitle>{reward?.id ? "Editar recompensa" : type === "discount" ? "Novo desconto" : "Novo produto"}</DialogTitle>
          <DialogDescription>{type === "discount" ? "O cliente troca pontos por um valor de desconto no pedido." : "O cliente troca pontos pelo produto grátis no pedido."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {type === "discount" ? <Field label="Valor do desconto (R$)"><Input type="number" min="0" step="0.01" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="reward-value-input" /></Field>
            : <ProductList products={products} value={f.product_id} onPick={(id) => setF({ ...f, product_id: id })} />}
          <Field label="Pontos necessários"><Input type="number" min="1" step="1" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} data-testid="reward-points-input" /></Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="reward-cancel-btn">Cancelar</Button>
          <Button disabled={!ok || busy} onClick={save} data-testid="reward-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
